import { useCallback, useEffect, useState } from 'react'
import {
  Box, Container, Typography, Chip, Alert, Button, Skeleton, alpha,
} from '@mui/material'
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { IconRefresh } from '@tabler/icons-react'
import { tokens } from '@/theme'
import { apiUrl } from '@/lib/apiBase'

// ─── Types (mirror api/schemas/basins.py :: BasinOut) ─────────────────────────
interface Basin {
  basin_id: number
  name: string
  subbasin: string | null
  state_water_board_code: string | null
  priority_level: string | null
  is_critically_overdrafted: boolean | null
  state_intervention_status: string | null
  source_url: string | null
  created_at: string | null
  updated_at: string | null
}

// Standard paginated envelope (mirrors api/pagination.py :: Page).
interface Page<T> {
  items: T[]
  page: number
  page_size: number
  total: number
  pages: number
}

const PRIORITY_COLOR: Record<string, string> = {
  'High':        tokens.green.dark,
  'Very High':   tokens.green.dark,
  'Medium':      tokens.blue.main,
  'Low':         tokens.green.main,
  'Very Low':    tokens.green.main,
}

function priorityColor(level: string | null): string {
  return PRIORITY_COLOR[level ?? ''] ?? tokens.blue.main
}

// ─── DataGrid columns ─────────────────────────────────────────────────────────
const BASIN_COLUMNS: GridColDef<Basin>[] = [
  { field: 'name', headerName: 'Basin', flex: 2, minWidth: 200 },
  {
    field: 'subbasin', headerName: 'Subbasin', flex: 2, minWidth: 180,
    valueGetter: (value: string | null) => value ?? '—',
  },
  {
    field: 'state_water_board_code', headerName: 'Code', width: 110,
    valueGetter: (value: string | null) => value ?? '—',
  },
  {
    field: 'priority_level', headerName: 'Priority', width: 130,
    renderCell: (p: GridRenderCellParams<Basin, string | null>) =>
      p.value ? (
        <Chip
          label={p.value}
          size="small"
          sx={{
            background: alpha(priorityColor(p.value), 0.1),
            color: priorityColor(p.value),
            fontFamily: '"Poppins", system-ui, sans-serif',
            fontSize: '0.65rem',
          }}
        />
      ) : null,
  },
  {
    field: 'state_intervention_status', headerName: 'State Status', width: 150,
    renderCell: (p: GridRenderCellParams<Basin, string | null>) =>
      p.value ? (
        <Chip
          label={p.value}
          size="small"
          sx={{
            background: alpha(tokens.blue.main, 0.1),
            color: tokens.blue.main,
            fontFamily: '"Poppins", system-ui, sans-serif',
            fontSize: '0.65rem',
          }}
        />
      ) : null,
  },
]

// ─── Component ────────────────────────────────────────────────────────────────
export default function DataDashboard() {
  const [basins, setBasins] = useState<Basin[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    // Live basin data behind the dev proxy (/api/agro/* -> FastAPI :8077).
    fetch(apiUrl('/api/agro/basins?is_critically_overdrafted=true&page_size=10'))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: Page<Basin>) => setBasins(data.items))
      .catch(() => setError('Failed to load basin data. Please try again.'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="overline" sx={{ color: tokens.blue.main, display: 'block', mb: 1 }}>
          Reference Data
        </Typography>
        <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.2rem' }, mb: 1 }}>
          Critically Overdrafted California Basins
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Basins designated as critically overdrafted under SGMA. Confirm current status with DWR or the relevant GSA.
        </Typography>
      </Box>

      {/* Loading */}
      {loading && (
        <Box>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Box>
      )}

      {/* Error */}
      {!loading && error && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<IconRefresh size={16} />}
              onClick={load}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Empty */}
      {!loading && !error && basins.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <Typography>No critically overdrafted basins to display.</Typography>
        </Box>
      )}

      {/* Data */}
      {!loading && !error && basins.length > 0 && (
        <DataGrid
          rows={basins}
          columns={BASIN_COLUMNS}
          getRowId={(row) => row.basin_id}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[10]}
          disableRowSelectionOnClick
          autoHeight
        />
      )}
    </Container>
  )
}
